export const enWallet = {
  status: {
    offline: 'Offline',
    online: 'Online',
    wrongNetwork: 'Wrong network',
  },
  connect: 'Connect wallet',
  connectAction: 'Connect wallet',
  disconnectAction: 'Disconnect wallet',
  switchNetworkAction: 'Switch network',
  openAccount: 'Open account',
  openConnect: 'Connect wallet',
  connectToSeeBalances: 'Connect your wallet to see on-chain balances.',
  keysStayInWallet: 'Your keys stay in your wallet.',
  session: 'Session',
  error: {
    connectToContinue: 'Connect your wallet to continue.',
    noValidOperation: 'There is no valid operation to execute.',
    networkUnavailable: 'Network is unavailable. Check your connection and try again.',
    connectorNotFound: 'Connected wallet not found. Reconnect MetaMask.',
    signingPrepareFailed: 'Could not prepare wallet signature. Open MetaMask and try again.',
    wrongNetworkMessage: (chainName: string, chainId: number, connectedChainId: number) =>
      `Please switch your wallet to ${chainName} (chain ID ${chainId}). Connected: ${connectedChainId}.`,
    userRejected: 'You rejected the signature in MetaMask.',
    chainMismatch: 'MetaMask is on another network. Switch to the app network and try again.',
    metamaskNoResponse:
      'MetaMask did not respond. Open MetaMask, return to the app and tap Convert again.',
    chainPrepareFailed: 'Could not prepare network for signing. Reload the app and try again.',
    txSendFailed: 'Could not send transaction to MetaMask. Open the app and try again.',
    walletNotFound: 'Connected wallet not found.',
    addChainRejected: (chainName: string) =>
      `You must accept adding network ${chainName} in MetaMask to continue.`,
    configureChainFailed:
      'Could not configure network in MetaMask. Open MetaMask, accept adding the network and try again.',
  },
} as const

export const enPortfolio = {
  connectTitle: 'Connect your wallet',
  connectDescription:
    'Link your wallet to view balances, assets and your on-chain portfolio summary.',
  tab: { summary: 'Summary', movements: 'Activity' },
  nativeBalance: 'Native balance',
  totalBalance: 'Total balance',
  walletBalance: 'Wallet balance',
  assetsInWallet: 'Assets in wallet',
  connectToListTokens: 'Connect your wallet to list your tokens.',
  noVisibleBalances: 'No visible balances in tracked tokens.',
  lpPositions: 'LP positions',
  manageLiquidity: 'Manage your liquidity',
  bridgeTitle: 'Bridge',
  bridgeSubtitle: 'Move assets across chains',
  openPortfolio: 'Open portfolio',
  sectionTitle: 'Your portfolio',
  sectionSubtitle: 'Balances and activity in your wallet',
  connectForAggregated: 'Connect your wallet to see aggregated value',
  movements: {
    empty:
      'No movements recorded from this app yet. Swaps made here will appear once submitted to the network.',
    noFilterResults: 'No movements for this filter.',
  },
} as const

export const enExplore = {
  tab: { tokens: 'Tokens', markets: 'Markets', pools: 'Pools' },
  search: {
    clear: 'Clear search',
    tokens: {
      title: 'No assets found',
      description: 'Try another symbol, name or contract address.',
    },
    markets: {
      title: 'No pairs found',
      description: 'Try another pair or token symbol.',
    },
  },
  tokenScope: { catalog: 'Catalog', imported: 'Imported', all: 'All' },
  token: {
    catalogTitle: 'Ecosystem catalog',
    importedTitle: 'Imported tokens',
    importedEmpty: 'You have no imported tokens. Search by contract to add one.',
    catalogEmpty: 'No tokens in the catalog.',
  },
  markets: {
    searchPlaceholder: 'Search pairs or assets',
    dynamicsTitle: 'Market dynamics',
    filter: { up: 'Trending up', down: 'Trending down' },
    pairsTitle: 'Pairs',
    noFilterResults: 'No pairs for this filter.',
  },
  pools: {
    myLpPositions: 'My LP positions',
    activePools: 'Active pools',
    aggregatedTvl: 'Aggregated TVL',
    sortByTvl: 'By TVL',
    sortByVolume: 'By volume',
    sortByApy: 'By APY',
    ecosystemTitle: 'Ecosystem pools',
  },
} as const

export const enHomeDashboard = {
  prelaunch: {
    kicker: 'Temporary notice',
    headline: 'Official launch in preparation',
    lead: 'Our DEX is preparing for official launch. Follow our channels for updates and announcements.',
  },
  liveMarket: 'Live market',
  liveMarketSubtitle: 'Primary pair price trends',
  noPrimaryPairPrice: 'No price data for the primary pair.',
  activePairs: 'Active pairs',
  activePairsSubtitle: 'Movers and pairs with volume',
  topMover: 'Top mover',
  exploreAllPairs: 'All pairs',
  exploreVolume: '+ volume',
  viewAllMarkets: 'View all markets →',
  quickAccess: 'Quick access',
  quickAccessSubtitle: 'Frequent routes',
  quickAction: {
    swap: 'Swap',
    swapSub: 'On-chain exchange',
    exploreTokens: 'Explore tokens',
    exploreTokensSub: 'Ecosystem catalog',
    explorePools: 'Explore pools',
    explorePoolsSub: 'Liquidity and APY',
    markets: 'Markets',
    marketsSub: 'Moving pairs',
    bridge: 'Bridge',
    bridgeSub: 'Cross-chain transfers',
  },
  mobile: {
    trendingPairs: 'Trending pairs',
    quickAction: { explore: 'Explore', portfolio: 'Portfolio', markets: 'Markets', bridge: 'Bridge' },
  },
} as const

export const enShell = {
  quickActions: 'Quick actions',
  quickAction: { swap: 'Swap', explore: 'Explore', positions: 'Positions', bridge: 'Bridge' },
  commandPalette: {
    trigger: 'Search or jump to...',
    title: 'Command Palette',
    placeholder: 'Type a command or search markets...',
    quickActions: 'Quick Actions',
    openSwap: 'Open Swap',
    openBridge: 'Open Bridge',
    addLiquidity: 'Add liquidity',
    viewPortfolio: 'View portfolio',
    markets: 'Markets',
    noIndexedPairs: 'No indexed pairs.',
  },
  tokenSearch: {
    title: 'Search token',
    label: 'Search token',
    subtitle: 'Symbol, name or contract',
    aria: 'Search token by symbol, name or contract',
  },
  tokenPicker: {
    withBalance: 'With balance',
    withoutBalance: 'Without balance',
    noVisibleBalance: 'No visible balance',
    featured: 'Featured',
    all: 'All',
    tokens: 'Tokens',
  },
} as const
